import CustomChip from '@app/components/atoms/CustomChip/CustomChip'
import Section from '@app/components/atoms/Section/Section'
import CardWidget from '@app/components/CardWidget'
import ImageSlider from '@app/components/ImageSlider'
import ModalUpdateStatusCampaign from '@app/components/ModalUpdateStatusCampaign'
import { VOUCHER_STATUS } from '@app/constant/VoucherConstant'
import { VOUCHER_TYPE } from '@app/constant/VoucherTypeConstant'
import { toDateTime, toRemainingTime, formatPrice, formatNumber } from '@app/libs/format'
import theme from '@app/libs/theme'
import useRemember from '@app/libs/useRemember'
import { Inertia } from '@inertiajs/inertia'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const IconButtonCustom = styled(IconButton)((props) => {
  if (props['aria-expanded']) {
    return {
      transform: 'rotate(180deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    }
  }
  return {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }
})

const VoucherDetail = ({ voucher }) => {
  const { t } = useTranslation()

  const [allExpandedVoucher, setAllExpandedVoucher] = useRemember(`VoucherDetail:${voucher.id}`, {
    description: true,
    guide: true,
    condition: true,
    term: true,
  })

  const handleExpand = (attribute) => {
    setAllExpandedVoucher({ ...allExpandedVoucher, [attribute]: !allExpandedVoucher[attribute] })
  }

  const [openModalConfirm, setOpenModalConfirm] = useState(false)
  const handleCloseModal = () => setOpenModalConfirm(false)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ImageSlider images={voucher.images} />
            </Grid>
            <Grid item xs={8}>
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Typography variant="h4">{voucher.name}</Typography>
                </Grid>

                <ModalUpdateStatusCampaign
                  content={
                    VOUCHER_STATUS.ACTIVE === voucher.status
                      ? t('MODAL.DEACTIVE_CONTENT_VOUCHER')
                      : t('MODAL.ACTIVE_CONTENT_VOUCHER')
                  }
                  handleCloseModal={handleCloseModal}
                  handleSubmit={() => {
                    Inertia.post(
                      route('merchant.vouchers.status.change', { voucher: voucher.id, status: voucher.status })
                    )
                    handleCloseModal()
                  }}
                  openModalConfirm={openModalConfirm}
                  title={
                    VOUCHER_STATUS.ACTIVE === voucher.status ? t('MODAL.DEACTIVE_VOUCHER') : t('MODAL.ACTIVE_VOUCHER')
                  }
                />

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.MERCHANT_NAME')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{voucher.merchant.name}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.TYPE_VOUCHER')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <CustomChip color={voucher.type} label={t(`VOUCHER.${voucher.type.toUpperCase()}`)} />
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.PRICE')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{formatPrice(voucher.price)}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.QUANTITY')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{formatNumber(voucher.quantity)}</Typography>
                </Grid>

                {VOUCHER_TYPE.PREMIUM === voucher.type && (
                  <>
                    <Grid item xs={3}>
                      <Typography color="textSecondary" variant="body1">
                        {t('VOUCHER.MIN')}
                      </Typography>
                    </Grid>

                    <Grid item xs={9}>
                      <Typography variant="body1">{voucher.limit_min}</Typography>
                    </Grid>

                    <Grid item xs={3}>
                      <Typography color="textSecondary" variant="body1">
                        {t('VOUCHER.MAX')}
                      </Typography>
                    </Grid>

                    <Grid item xs={9}>
                      <Typography variant="body1">{voucher.limit_max}</Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.CATEGORIES')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.categories.map((item) => (
                    <Chip key={item.id} label={item.name} style={{ margin: '2px' }} />
                  ))}
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.EXPIRED_DATE')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{toDateTime(voucher.expired_date)}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.REMAIN_TIME')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.expired_date ? (
                    toRemainingTime(new Date(), voucher.expired_date)
                  ) : (
                    <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                  )}
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.STATUS')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <CustomChip color={voucher.status} label={t(`VOUCHER.${voucher.status.toUpperCase()}`)} />
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.HASHTAGS')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.tags ? (
                    voucher.tags.map((item) => <Chip key={item.id} label={item.name} style={{ margin: '2px' }} />)
                  ) : (
                    <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item lg={3} md={6} xs={12}>
            <CardWidget
              color="warning"
              primary={voucher.codes_redeem + voucher.codes_used}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_get_price}
              title={t('VOUCHER.TOTAL_GET_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} xs={12}>
            <CardWidget
              color="success"
              primary={voucher.codes_redeem}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_redeem_price}
              title={t('VOUCHER.REDEEMED_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} xs={12}>
            <CardWidget
              color="danger"
              primary={voucher.codes_used}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_used_price}
              title={t('VOUCHER.USED_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} xs={12}>
            <CardWidget
              primary={voucher.quantity - (voucher.codes_redeem + voucher.codes_used)}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.availablePoints}
              title={t('VOUCHER.REMAIN')}
            ></CardWidget>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.DESCRIPTION')}{' '}
                <IconButtonCustom
                  aria-expanded={allExpandedVoucher.description}
                  aria-label="show more"
                  onClick={() => handleExpand('description')}
                >
                  <ExpandMore />
                </IconButtonCustom>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.description} timeout="auto">
                {voucher.description ? (
                  <Typography
                    dangerouslySetInnerHTML={{ __html: voucher.description }}
                    sx={{ wordBreak: 'break-word', textAlign: 'justify' }}
                    variant="body1"
                  ></Typography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.HOW_TO_USE')}{' '}
                <IconButtonCustom
                  aria-expanded={allExpandedVoucher.guide}
                  aria-label="show more"
                  onClick={() => handleExpand('guide')}
                >
                  <ExpandMore />
                </IconButtonCustom>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.guide} timeout="auto">
                {voucher.how_to_use ? (
                  <Typography
                    dangerouslySetInnerHTML={{ __html: voucher.how_to_use }}
                    sx={{ wordBreak: 'break-word', textAlign: 'justify' }}
                    variant="body1"
                  ></Typography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.TERM_AND_CONDITION')}{' '}
                <IconButtonCustom
                  aria-expanded={allExpandedVoucher.condition}
                  aria-label="show more"
                  onClick={() => handleExpand('condition')}
                >
                  <ExpandMore />
                </IconButtonCustom>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.condition} timeout="auto">
                {voucher.condition ? (
                  <Typography
                    dangerouslySetInnerHTML={{ __html: voucher.condition }}
                    sx={{ wordBreak: 'break-word', textAlign: 'justify' }}
                    variant="body1"
                  ></Typography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>
    </Grid>
  )
}

export default VoucherDetail
